import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { listOpsReminders, patchOpsReminder, acknowledgeOpsNotification } from "@/lib/opsPaperworkClient";
import {
  filterRemindersForSurface,
  mergeChecklistShellReminders,
  type RawWorkplaceNotificationRow,
  type WorkplaceReminderDeepLink,
  type WorkplaceReminderViewModel,
} from "@/lib/messages/workplaceReminderModel";
import type { ReassignMemberOption } from "./WorkplaceReminderHygieneActions";

export type UseWorkplaceRemindersOptions = {
  enabled?: boolean;
  surface?: "bell" | "checklist" | "lead";
  leadId?: string | null;
  orgManagerNudge?: { title: string; body: string; sent_at: string } | null;
  reassignMembers?: ReassignMemberOption[];
};

export function useWorkplaceReminders(options: UseWorkplaceRemindersOptions = {}) {
  const {
    enabled = true,
    surface = "bell",
    leadId = null,
    orgManagerNudge = null,
    reassignMembers = [],
  } = options;

  const auth = useAuth();
  const canReassign = useMemo(
    () =>
      auth.hasRole(
        "trainer",
        "marketing",
        "admin",
        "owner",
        "dev",
        "ads_guidance_editor",
        "platform_admin",
        "platform_dev",
        "platform_trainer"
      ),
    [auth]
  );

  const [reminders, setReminders] = useState<WorkplaceReminderViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!enabled) {
      setReminders([]);
      return;
    }
    setLoading(true);
    setError("");
    const r = await listOpsReminders();
    setLoading(false);
    if (!r.ok) {
      setError(r.error || "Unable to load reminders.");
      setReminders([]);
      return;
    }
    const rows = (r.data.reminders ?? []) as RawWorkplaceNotificationRow[];
    const merged = mergeChecklistShellReminders({
      notifications: rows,
      orgManagerNudge: surface === "checklist" ? orgManagerNudge : null,
    });
    setReminders(filterRemindersForSurface(merged, surface, leadId));
  }, [enabled, surface, leadId, orgManagerNudge]);

  useEffect(() => {
    void load();
  }, [load]);

  const dismiss = useCallback(
    async (notificationId: string) => {
      const res = await patchOpsReminder({ id: notificationId, action: "dismiss" });
      if (!res.ok) {
        setError(res.error || "Unable to dismiss reminder.");
        return;
      }
      await load();
    },
    [load]
  );

  const acknowledge = useCallback(
    async (notificationId: string) => {
      const res = await acknowledgeOpsNotification({ id: notificationId });
      if (!res.ok) {
        setError(res.error || "Unable to acknowledge.");
        return;
      }
      await load();
    },
    [load]
  );

  const resolve = useCallback(
    async (notificationId: string, reason?: string) => {
      const res = await patchOpsReminder({ id: notificationId, action: "resolve", reason });
      if (!res.ok) {
        setError(res.error || "Unable to resolve reminder.");
        return;
      }
      await load();
    },
    [load]
  );

  const reassign = useCallback(
    async (notificationId: string, assigneeUserId: string, reason?: string) => {
      const res = await patchOpsReminder({
        id: notificationId,
        action: "reassign",
        assignee_user_id: assigneeUserId,
        reason,
      });
      if (!res.ok) {
        setError(res.error || "Unable to reassign reminder.");
        return;
      }
      await load();
    },
    [load]
  );

  return {
    reminders,
    loading,
    error,
    load,
    dismiss,
    acknowledge,
    resolve,
    reassign,
    canReassign,
    reassignMembers,
  };
}

export type WorkplaceReminderNavigationHandlers = {
  onOpenLead?: (leadId: string) => void;
  onOpenThread?: (threadId: string) => void;
  onOpenChecklist?: () => void;
};

export function navigateWorkplaceReminderDeepLink(
  link: WorkplaceReminderDeepLink,
  handlers: WorkplaceReminderNavigationHandlers
): void {
  if (link.lead_id && handlers.onOpenLead) {
    handlers.onOpenLead(link.lead_id);
    return;
  }
  if (link.message_thread_id && handlers.onOpenThread) {
    handlers.onOpenThread(link.message_thread_id);
    return;
  }
  if (link.checklist_item_id && handlers.onOpenChecklist) {
    handlers.onOpenChecklist();
  }
}
